"use strict";
// Money/date/status formatting shared by Overview's recent-order card and
// the Orders list/detail screens — factored out once a second real
// consumer needed the exact same MoneyV2 formatting logic, rather than
// duplicated a second time.
(function () {
    /**
     * Escapes for BOTH text and quoted attributes.
     *
     * This used to be `div.textContent = value; return div.innerHTML`, which
     * is the serialisation of a text node — and that escapes &, < and > but
     * NOT quotes, because a text node has no attribute to break out of. The
     * Hub then interpolated the result into quoted attributes in 64 places.
     * A display name of `x" data-injected="1` closed the attribute and
     * opened a new one; proven in a browser before this was changed.
     *
     * Both quote styles are escaped, so the output is safe in a
     * double-quoted attribute, a single-quoted one, and in text. The
     * ampersand has to go first or it would double-escape the entities the
     * later replacements introduce.
     */
    function escapeHtml(value) {
        return String(value)
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#39;");
    }
    function money(input, locale) {
        var amount = parseFloat(input.amount);
        try {
            return new Intl.NumberFormat(locale, { style: "currency", currency: input.currencyCode }).format(amount);
        }
        catch (err) {
            // An unrecognized/malformed currency code from Shopify shouldn't
            // throw and blank out the surrounding card — fall back to a plain label.
            return input.amount + " " + input.currencyCode;
        }
    }
    function date(iso, locale, options) {
        try {
            return new Intl.DateTimeFormat(locale, options || { dateStyle: "medium" }).format(new Date(iso));
        }
        catch (err) {
            return iso;
        }
    }
    /**
     * Shopify's real displayFinancialStatus/displayFulfillmentStatus values
     * (e.g. "PARTIALLY_REFUNDED"), turned into readable text — never a new
     * status *value*, only presentation of the real one. Per ADR 015, the
     * backend deliberately passes these through raw with no unified enum;
     * deciding how to *display* them is explicitly the frontend's job.
     */
    function humanizeStatus(value) {
        if (!value)
            return null;
        return value
            .toLowerCase()
            .split("_")
            .map(function (word) {
            return word.charAt(0).toUpperCase() + word.slice(1);
        })
            .join(" ");
    }
    function initials(displayName) {
        if (!displayName || displayName.trim().length === 0)
            return "?";
        var parts = displayName.trim().split(/\s+/);
        var first = parts[0] ? parts[0].charAt(0) : "";
        var second = parts.length > 1 && parts[1] ? parts[1].charAt(0) : "";
        return (first + second).toUpperCase() || "?";
    }
    /**
     * Enough of an address to recognise, not enough to read over a shoulder.
     * Used by Overview's header and by Connected Accounts, which is why it
     * lives here rather than privately in either.
     */
    function maskEmail(email) {
        var at = email.indexOf("@");
        if (at <= 0)
            return email;
        var local = email.slice(0, at);
        var domain = email.slice(at);
        return local.slice(0, Math.min(2, local.length)) + "***" + domain;
    }
    /**
     * The avatar to draw, from a profile that may not describe one.
     *
     * The theme and the identity service deploy independently. A theme that
     * ships first sees a `GET /me` with no `avatar` field at all, and reading
     * `profile.avatar.source` on that response throws — which does not
     * degrade the picture, it takes out the whole Overview and Profile panel
     * and leaves them on "Loading…". This test suite found exactly that, in
     * 94 specs at once.
     *
     * So a missing, malformed or unrecognised avatar resolves to `default`:
     * the store logo, with initials behind it. That is the correct rendering
     * for an account with no picture anyway, which is why it is a safe thing
     * to fall back to rather than a guess.
     */
    function resolvedAvatar(profile) {
        var avatar = profile.avatar;
        if (!avatar || typeof avatar !== "object") {
            return { source: "default", url: null, updatedAt: null };
        }
        if (["custom", "provider_google", "provider_discord", "default"].indexOf(avatar.source) === -1) {
            return { source: "default", url: null, updatedAt: null };
        }
        return avatar;
    }
    window.FlixHubFormat = {
        resolvedAvatar: resolvedAvatar,
        money: money,
        date: date,
        humanizeStatus: humanizeStatus,
        escapeHtml: escapeHtml,
        maskEmail: maskEmail,
        initials: initials,
    };
})();
